import BootstrapDropdown from 'react-bootstrap/lib/Dropdown'
import FormGroup from 'react-bootstrap/lib/FormGroup'
import Icon, {icons} from './icon'
import MenuItem from 'react-bootstrap/lib/MenuItem'
import PropTypes from 'prop-types'
import React, {PureComponent} from 'react'
import find from 'lodash/find'
import {colors, common, text} from '../styles'
import {css} from 'glamor'

const style = {
  dropdown: css({
    display: 'inline-block',
    position: 'relative',
    '&.open > .dropdown-toggle, &.open > .dropdown-toggle:focus': {
      borderColor: colors.inputBorderFocus,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    }
  }),
  dropdownBlock: css({
    width: '100%'
  }),
  dropdownToggle: css(text.input, {
    background: `${colors.inputFocus} !important`,
    border: `1px solid ${colors.inputBorder}`,
    boxShadow: 'none !important',
    color: colors.inputText,
    display: 'inline-block',
    height: '50px',
    padding: '5px 20px 5px',
    position: 'relative',
    textAlign: 'left',
    width: '100%',
    '& > .caret': {
      display: 'none'
    },
    '&:focus': {
      borderColor: colors.inputBorderFocus
    }
  }),
  dropdownToggleColorSwatch: css({
    paddingLeft: '50px',
    '& > .dropdown-label': {
      left: '50px'
    }
  }),
  dropdownToggleNoLabel: css({
    paddingTop: '5px'
  }),
  dropdownLarge: css({
    width: '300px'
  }),
  dropdownSmall: css({
    width: '150px'
  }),
  dropdownInvalid: css({
    borderColor: colors.inputBorderError
  }),
  formGroup: css({
    marginBottom: '10px'
  }),
  icon: css({
    color: colors.inputIcon,
    pointerEvents: 'none',
    position: 'absolute',
    top: '13px',
    right: '20px'
  }),
  iconInvalid: css({
    color: colors.inputBorderError
  }),
  label: css(text.inputLabel, {
    color: colors.inputPlaceholder,
    pointerEvents: 'none',
    position: 'absolute',
    left: '20px',
    top: '9px'
  }),
  menu: css({
    border: `1px solid ${colors.inputBorderFocus}`,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: '3px',
    borderBottomRightRadius: '3px',
    boxShadow: 'none',
    margin: '-1px 0 0',
    padding: 0,
    width: '100%',
    maxHeight: '250px',
    overflow: 'scroll'
  }),
  menuItem: css({
    borderBottom: `1px solid ${colors.inputBorder}`,
    padding: '10px 20px !important',
    textTransform: 'capitalize',
    width: '100%',
    '&:hover': {
      background: colors.inputBG
    }
  }),
  menuItemColorSwatch: css({
    paddingLeft: '16px !important'
  }),
  value: css({
    textTransform: 'capitalize',
    '&:empty + .dropdown-label': css(text.input, {
      top: '13px'
    })
  }),
  withLabel: css({
    paddingTop: '18px'
  }),
  get select() {
    return css(text.input, style.formGroup, {
      position: 'relative',
      '& > select': {
        outline: 'none',
        WebkitAppearance: 'none',
        '&::-ms-expand': {
          display: 'none'
        }
      }
    })
  }
}

export default class Dropdown extends PureComponent {
  onChange = (eventKey) => {
    const {onChange, name} = this.props

    // Mimic the form input onChange API, which expects an event object.
    if (onChange) {
      onChange({
        target: {
          name,
          value: eventKey.target
            ? eventKey.target.value
            : eventKey.value || eventKey
        }
      })
    }
  }

  optionsAreColors() {
    const [option] = this.props.options || []
    return option && option.color
  }

  render() {
    const {
      disabled,
      id,
      label,
      options,
      required,
      size,
      style: customStyle,
      valid,
      value
    } = this.props

    let elementStyles = style.dropdownToggle
    let iconStyles = style.icon
    let wrapperStyles = style.dropdown
    let colorSwatch

    switch (size) {
      case 'small':
        wrapperStyles = css(wrapperStyles, style.dropdownSmall)
        break

      case 'block':
        wrapperStyles = css(wrapperStyles, style.dropdownBlock)
        break

      case 'large':
      default:
        wrapperStyles = css(wrapperStyles, style.dropdownLarge)
        break
    }

    const useNativeControl = options && !this.optionsAreColors()

    if (this.optionsAreColors() && value) {
      colorSwatch = find(options, {value: value.toLowerCase()})
      elementStyles = css(elementStyles, style.dropdownToggleColorSwatch)
    }

    if (!label) {
      elementStyles = css(elementStyles, style.dropdownToggleNoLabel)
    }

    if (valid === false) {
      elementStyles = css(elementStyles, style.dropdownInvalid)
      iconStyles = css(iconStyles, style.iconInvalid)
    }

    if (label && value) {
      elementStyles = css(elementStyles, style.withLabel)
    }

    wrapperStyles = css(wrapperStyles, customStyle)

    if (useNativeControl) {
      return (
        <>
          <FormGroup {...style.select}>
            <select
              id={id}
              onChange={this.onChange}
              value={value || ''}
              required={required}
              {...elementStyles}
            >
              {!value && (
                <option disabled value="">
                  {label}
                </option>
              )}
              {options.map((option) => {
                const value = option.value != null ? option.value : option
                return (
                  <option disabled={option.disabled} key={value} value={value}>
                    {option.label || value}
                  </option>
                )
              })}
            </select>
            {value && label && (
              <span className="dropdown-label" {...style.label}>
                {label}
              </span>
            )}
            <div className="chevron" {...iconStyles}>
              <Icon icon={icons.chevronDown} />
            </div>
          </FormGroup>
        </>
      )
    }

    return (
      <>
        <FormGroup {...style.formGroup}>
          <BootstrapDropdown
            id={id}
            onSelect={this.onChange}
            disabled={disabled}
            required={required}
            {...wrapperStyles}
          >
            <BootstrapDropdown.Toggle {...elementStyles}>
              {colorSwatch && (
                <div
                  {...css(common.colorSwatch, common.colorSwatchValue, {
                    background: colorSwatch.color
                  })}
                />
              )}
              <span {...style.value}>
                {colorSwatch ? colorSwatch.value : value}
              </span>
              {label && (
                <span className="dropdown-label" {...style.label}>
                  {label}
                </span>
              )}
              <div className="chevron" {...iconStyles}>
                <Icon icon={icons.chevronDown} />
              </div>
            </BootstrapDropdown.Toggle>
            <BootstrapDropdown.Menu {...style.menu}>
              {options &&
                options.map((option) => {
                  return (
                    <MenuItem
                      eventKey={option.value}
                      key={option.value}
                      {...css(
                        style.menuItem,
                        option.color ? style.menuItemColorSwatch : {}
                      )}
                    >
                      {option.color && (
                        <div
                          {...css(common.colorSwatch, {
                            background: option.color
                          })}
                        />
                      )}
                      {option.label || option.value}
                    </MenuItem>
                  )
                })}
              {!options && this.props.children}
            </BootstrapDropdown.Menu>
          </BootstrapDropdown>
        </FormGroup>
      </>
    )
  }
}

Dropdown.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  colorSwatch: PropTypes.shape({
    color: PropTypes.string,
    value: PropTypes.string
  }),
  disabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'large', 'block']),
  style: PropTypes.object,
  valid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

Dropdown.defaultProps = {
  disabled: false,
  size: 'large'
}
